import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';

import LoaderGif from '@components/common/LoaderGif/LoaderGif';
import ModalHeader from '@components/modals/ModalHeader/ModalHeader';
import Button from '@components/uikit/Button/Button';
import TreeSelect from '@components/uikit/TreeSelect/TreeSelect';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useLocationsDeleteAssetMutation } from '@pages/ManageLocations/services/useLocationsDeleteAssetMutation';

import { useEquipmentsForLocationQuery } from '@services/equipment/useEquipmentsForLocationQuery';
import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useLocationsGetOneQuery } from '@services/useLocationsGetOneQuery';

import { useModalActions } from '@store/useModalStore';

import style from './style.module.scss';

const LocationDeleteModal = ({ locationId, locationName }) => {
  const { customerId } = useParams();
  const { t } = useTranslation();
  const { setModalClose } = useModalActions();

  const { data: assetTreeData } = useAssetTreeQuery();
  const { data: dataOneLocation } = useLocationsGetOneQuery({ locationId });

  const { data: equipmentData, isLoading: equipmentDataIsLoading } = useEquipmentsForLocationQuery({
    locationId,
  });

  const { mutate: locationDeleteAssetMutate, isLoading: locationDeleteIsLoading } =
    useLocationsDeleteAssetMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({
    mode: 'all',
    defaultValues: {
      new_location: dataOneLocation?.parent.id || null,
    },
  });

  const handleDelete = handleSubmit((formData) => {
    locationDeleteAssetMutate(
      {
        id: locationId,
        new_location: formData.new_location,
      },
      {
        onSuccess: ({ data }) => {
          if (data?.status !== 'error') {
            showToast({ variant: 'success', message: t('Asset was deleted successfully.') });
          } else {
            showToast({ variant: 'error', message: t(data?.errors.name || 'Something went wrong!') });
          }
        },
        onSettled: () => handleClose(),
      },
    );
  });

  const handleClose = () => {
    setModalClose();
    reset();
  };

  const treeData = useMemo(() => {
    if (!dataOneLocation) {
      return [];
    }

    const getChildren = (children) => {
      if (!children.length) {
        return [];
      }
      return children.map((loc) => ({
        ...loc,
        disabled: loc.id === locationId,
        children: getChildren(loc.children),
      }));
    };

    return assetTreeData.facilities.reduce((acc, loc) => {
      if (loc.facilityId === dataOneLocation.facility_id) {
        return acc.concat({
          ...loc,
          children: getChildren(loc.children),
        });
      }
      return acc;
    }, []);
  }, [dataOneLocation, assetTreeData]);

  return (
    <div className={style.locationDeleteModal}>
      <ModalHeader onClose={handleClose} />
      <div className={style.modalBody}>
        <div className={style.modalBodyTitle}>
          {t('Delete Location')} <br />"{locationName}"
        </div>
        <div className={style.modalBodyFirstConfirmText}>
          {t('Are you sure you want to delete the selected location?')}
        </div>

        {equipmentDataIsLoading && <LoaderGif className={style.loader} />}
        {equipmentData?.length ? (
          <>
            <div className={style.modalBodySecondConfirmText}>
              {t('It has {count} equipment installed.', { count: equipmentData?.equipments?.length })}{' '}
              {t('You can view the list of equipment on the')}{' '}
              <Link className={style.link} to={`/customer/${customerId}/manage-equipment`} target="_blank">
                {t('Manage Equipment')}
              </Link>{' '}
              {t('page')}
              <div>
                {t('To proceed with the deletion, please select a location to move the equipment to.')}
              </div>
            </div>
            <TreeSelect
              items={treeData}
              name="new_location"
              searchable
              defaultSelectedName={dataOneLocation?.parent.name || ''}
              control={control}
              fieldNames={{
                children: 'children',
                title: 'name',
                key: 'id',
              }}
              placeholder={t('Start To Type...')}
              defaultExpandAll
              type={'woHelperText'}
            />
          </>
        ) : null}
      </div>
      <div className={style.modalFooter}>
        <Button
          type="primary"
          text={t('Delete')}
          disabled={isSubmitting || locationDeleteIsLoading}
          onClick={handleDelete}
        />
        <Button type="secondary" text={t('Cancel')} onClick={handleClose} />
      </div>
    </div>
  );
};

export default LocationDeleteModal;
